import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useTypewriter } from "@/hooks/useTypewriter";

interface TypewriterLineProps {
  /** Already-translated message(s). One message types once and holds; several
   * rotate (type → 4 caret blinks → backspace → next). */
  messages: readonly string[];
  /** `lg` = production surfaces (operators read at distance). */
  size?: "sm" | "lg";
  className?: string;
}

/** After this long the line stops narrating progress and says so — a loader
 * looping forever must read as "something is wrong", not "still fine". */
const GIVE_UP_MS = 20_000;

/**
 * The typed loading line shared by every loading surface (DESIGN.md §15.7):
 * one stable screen-reader announcement per load, the animated text hidden
 * from AT, and a caret that blinks in `caret-blink` steps while dwelling.
 * The caret remounts per message (`key`) so the blink starts in phase.
 */
export function TypewriterLine({ messages, size = "sm", className }: TypewriterLineProps) {
  const { t } = useTranslation("loading");
  // Give-up resets whenever the messages change (e.g. an auth phase advance
  // after a slow boot) — real progress must never stay stuck on the give-up.
  const messagesKey = messages.join(" ");
  const [givenUp, setGivenUp] = useState(false);
  useEffect(() => {
    setGivenUp(false);
    const id = window.setTimeout(() => setGivenUp(true), GIVE_UP_MS);
    return () => window.clearTimeout(id);
  }, [messagesKey]);
  const resolved = givenUp ? [t("stillWorking")] : messages;
  const { text, phase, messageIndex } = useTypewriter(resolved);

  return (
    <div role="status" aria-busy="true" className={cn("flex items-center", className)}>
      {/* One stable announcement per load; the animated line is presentation only. */}
      <span className="sr-only">{resolved[0] ?? t("pool.generic.1")}</span>
      <span
        aria-hidden="true"
        className={cn(
          "font-mono tracking-tight text-muted-foreground whitespace-nowrap",
          size === "lg" ? "text-base" : "text-sm",
        )}
      >
        {text}
        <span
          key={messageIndex}
          className={cn(
            "ml-px inline-block w-px bg-primary align-middle",
            size === "lg" ? "h-4" : "h-3.5",
            phase === "dwell" ? "animate-caret-blink" : "opacity-100",
          )}
        />
      </span>
    </div>
  );
}
